// =============================================================================
// FIELD PALETTE - Professional draggable fields list like SurveyJS
// =============================================================================

import { useState, useMemo } from 'react';
import {
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  CalendarClock,
  Upload,
  ImagePlus,
  Image,
  PenTool,
  Star,
  Gauge,
  SlidersHorizontal,
  ListOrdered,
  Grid3X3,
  Heading,
  Text,
  Code,
  LayoutTemplate,
  PanelTop,
  Minus,
  Link,
  Lock,
  ToggleLeft,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { FIELD_TEMPLATES, CATEGORY_LABELS, type FieldTemplate, type FieldType } from '../types';
import { useFormBuilderStore } from '../store';

const ICON_MAP: Record<string, LucideIcon> = {
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  CalendarClock,
  Upload,
  ImagePlus,
  Image,
  PenTool,
  Star,
  Gauge,
  SlidersHorizontal,
  ListOrdered,
  Grid3X3,
  Heading,
  Text,
  Code,
  LayoutTemplate,
  PanelTop,
  Minus,
  Link,
  Lock,
  ToggleLeft,
};

interface DraggableFieldProps {
  template: FieldTemplate;
  onDragStart: (event: React.DragEvent, fieldType: FieldType) => void;
  onClick: () => void;
}

function DraggableField({ template, onDragStart, onClick }: DraggableFieldProps) {
  const Icon = ICON_MAP[template.icon] || Type;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, template.type)}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 rounded-lg border border-secondary-200 bg-white p-3 cursor-grab transition-all',
        'hover:border-primary-400 hover:shadow-md hover:bg-primary-50/50',
        'dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-600 dark:hover:bg-secondary-700',
        'active:cursor-grabbing active:scale-[0.98]'
      )}
    >
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg',
        'bg-secondary-100 group-hover:bg-primary-100',
        'dark:bg-secondary-700 dark:group-hover:bg-primary-900/30'
      )}>
        <Icon className="h-5 w-5 text-secondary-600 group-hover:text-primary-600 dark:text-secondary-400 dark:group-hover:text-primary-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-secondary-800 dark:text-secondary-200 truncate">
          {template.labelFr}
        </p>
        <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
          {template.descriptionFr}
        </p>
      </div>
    </div>
  );
}

interface FieldPaletteProps {
  onDragStart: (event: React.DragEvent, fieldType: FieldType) => void;
  onAddField: (fieldType: FieldType) => void;
}

export function FieldPalette({ onDragStart, onAddField }: FieldPaletteProps) {
  const { searchQuery, setSearchQuery } = useFormBuilderStore();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    basic: true,
    selection: true,
    datetime: true,
    advanced: true,
    display: true,
    layout: true,
  });

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return FIELD_TEMPLATES;

    const query = searchQuery.toLowerCase();
    return FIELD_TEMPLATES.filter(
      (template) =>
        template.label.toLowerCase().includes(query) ||
        template.labelFr.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query) ||
        template.descriptionFr.toLowerCase().includes(query) ||
        template.type.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group templates by category
  const groupedTemplates = useMemo(() => {
    return filteredTemplates.reduce<Record<string, FieldTemplate[]>>(
      (acc, template) => {
        const category = acc[template.category];
        if (!category) {
          acc[template.category] = [template];
        } else {
          category.push(template);
        }
        return acc;
      },
      {}
    );
  }, [filteredTemplates]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const categoryOrder = ['basic', 'selection', 'datetime', 'advanced', 'display', 'layout'];

  return (
    <div className="flex flex-col h-full bg-secondary-50/50 dark:bg-secondary-900/50">
      {/* Header */}
      <div className="p-4 border-b border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800">
        <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">
          Champs de formulaire
        </h3>
        <p className="text-xs text-secondary-500 mb-3">
          Glissez ou cliquez pour ajouter
        </p>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <Input
            placeholder="Rechercher un champ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-secondary-50 dark:bg-secondary-900"
          />
        </div>
      </div>

      {/* Content with scrollbar */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2 scrollbar-thin scrollbar-thumb-secondary-300 dark:scrollbar-thumb-secondary-600 hover:scrollbar-thumb-secondary-400 dark:hover:scrollbar-thumb-secondary-500">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary-500">Aucun champ trouvé</p>
          </div>
        ) : (
          categoryOrder.map((category) => {
            const templates = groupedTemplates[category];
            if (!templates || templates.length === 0) return null;

            const isExpanded = expandedCategories[category];
            const categoryLabel = CATEGORY_LABELS[category];

            return (
              <div key={category} className="rounded-lg overflow-hidden">
                {/* Category header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-left',
                    'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700',
                    'hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors'
                  )}
                >
                  <span className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 uppercase tracking-wider">
                    {categoryLabel?.fr || category}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-secondary-400 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {/* Category fields */}
                {isExpanded && (
                  <div className="mt-2 space-y-2">
                    {templates.map((template) => (
                      <DraggableField
                        key={template.type}
                        template={template}
                        onDragStart={onDragStart}
                        onClick={() => onAddField(template.type)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer with stats */}
      <div className="p-3 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800">
        <p className="text-xs text-center text-secondary-500">
          {FIELD_TEMPLATES.length} types de champs disponibles
        </p>
      </div>
    </div>
  );
}
